import { useCallback, useState } from "react";

import { FilterModelMap, SetFilterLabelResolver, setFilterValueKeys } from "~/helpers/grid/filterModelToText";

type MruSetFilterLabels = Record<string, Record<string, string>>;

export interface MruFilterEntry {
	displayText?: string;
	filterModel: FilterModelMap;
	id: string;
	setLabels?: MruSetFilterLabels;
	timestamp: number;
}

const MAX_ENTRIES = 10;

/**
 * Normalize a filter model for deduplication:
 * sort object keys and sort set filter values.
 */
function normalizeFilterModel(filterModel: FilterModelMap): string {
	const sorted = Object.keys(filterModel)
		.sort()
		.reduce<Record<string, unknown>>((acc, key) => {
			const entry = filterModel[key];
			// Deep clone and sort set filter values for consistency
			acc[key] = normalizeFilterEntry(entry);
			return acc;
		}, {});
	return JSON.stringify(sorted);
}

function normalizeFilterEntry(entry: unknown): unknown {
	if (!entry || typeof entry !== "object") return entry;

	const obj = entry as Record<string, unknown>;

	// Multi filter
	if (obj.filterType === "multi" && Array.isArray(obj.filterModels)) {
		return {
			...obj,
			filterModels: obj.filterModels.map((m: unknown) => normalizeFilterEntry(m))
		};
	}

	// Set filter - sort values for consistent comparison
	if (Array.isArray(obj.values)) {
		return {
			...obj,
			values: [...obj.values].sort()
		};
	}

	return entry;
}

const captureSetFilterLabels = (
	filterModel: FilterModelMap,
	labelResolver: SetFilterLabelResolver | undefined
): MruSetFilterLabels | undefined => {
	const labels: MruSetFilterLabels = {};
	for (const { field, key } of setFilterValueKeys(filterModel)) {
		const label = labelResolver?.(field, key);
		if (label !== undefined) labels[field] = { ...labels[field], [key]: label };
	}
	return Object.keys(labels).length > 0 ? labels : undefined;
};

export interface UseMruFiltersReturn {
	addEntry: (filterModel: FilterModelMap, labelResolver: SetFilterLabelResolver | undefined) => void;
	clearEntries: () => void;
	entries: MruFilterEntry[];
	removeEntry: (id: string) => void;
}

const useMruFilters = (gridDataKey: string): UseMruFiltersReturn => {
	const storageKey = `gridmru-${gridDataKey}`;

	const loadEntries = (): MruFilterEntry[] => {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return [];
		try {
			return JSON.parse(raw) as MruFilterEntry[];
		} catch {
			return [];
		}
	};

	const [entries, setEntries] = useState<MruFilterEntry[]>(loadEntries);

	const persist = useCallback(
		(next: MruFilterEntry[]) => {
			try {
				localStorage.setItem(storageKey, JSON.stringify(next));
			} catch {
				// QuotaExceededError — storage full, skip write
			}
			setEntries(next);
		},
		[storageKey]
	);

	const addEntry = useCallback(
		(filterModel: FilterModelMap, labelResolver: SetFilterLabelResolver | undefined) => {
			if (!filterModel || Object.keys(filterModel).length === 0) return;

			const normalized = normalizeFilterModel(filterModel);
			const prev = loadEntries();
			const firstEntry = prev[0];

			// Skip duplicate writes to avoid unnecessary re-renders during cell transitions.
			if (firstEntry && normalizeFilterModel(firstEntry.filterModel) === normalized) {
				return;
			}

			// Check for duplicate
			const existingIndex = prev.findIndex((e) => normalizeFilterModel(e.filterModel) === normalized);

			let next: MruFilterEntry[];
			if (existingIndex >= 0) {
				// Update timestamp and move to front
				const updated = { ...prev[existingIndex], timestamp: Date.now() };
				next = [updated, ...prev.filter((_, i) => i !== existingIndex)];
			} else {
				const newEntry: MruFilterEntry = {
					filterModel,
					id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
					setLabels: captureSetFilterLabels(filterModel, labelResolver),
					timestamp: Date.now()
				};
				next = [newEntry, ...prev].slice(0, MAX_ENTRIES);
			}

			persist(next);
		},
		[persist]
	);

	const removeEntry = useCallback(
		(id: string) => {
			const next = loadEntries().filter((e) => e.id !== id);
			persist(next);
		},
		[persist]
	);

	const clearEntries = useCallback(() => {
		persist([]);
	}, [persist]);

	return { addEntry, clearEntries, entries, removeEntry };
};

export default useMruFilters;
